import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './MarketData.css';

// Chart using Recharts
const PriceTrendChart = ({ transactions }) => {
  const chartData = useMemo(() => {
    if (!transactions || transactions.length < 2) return [];

    // 1. Filter and Sort
    const sortedData = [...transactions]
      .filter(t => {
        if (!t.pricePerMeter || !t.date) return false;
        return t.pricePerMeter >= 1000 && t.pricePerMeter <= 200000;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    if (sortedData.length < 2) return [];

    // 2. Apply Smoothing (Moving Average) to create a Trend Line
    // Dynamic window size: larger window for more data points to reduce noise
    const windowSize = Math.max(5, Math.floor(sortedData.length / 10));

    return sortedData.map((t, index) => {
      // Calculate centered moving average
      const start = Math.max(0, index - Math.floor(windowSize / 2));
      const end = Math.min(sortedData.length, index + Math.floor(windowSize / 2) + 1);
      const subset = sortedData.slice(start, end);
      const avgPrice = subset.reduce((sum, item) => sum + item.pricePerMeter, 0) / subset.length;

      const dateObj = new Date(t.date);
      // Format as "Sep 25" (short month + 2-digit year)
      const shortMonth = dateObj.toLocaleDateString('en-US', { month: 'short' });
      const shortYear = dateObj.getFullYear().toString().slice(-2);
      
      return {
        date: t.date,
        displayDate: `${shortMonth} ${shortYear}`,
        timestamp: dateObj.getTime(),
        price: Math.round(avgPrice), // Smoothed price
        fullDate: dateObj.toLocaleDateString('he-IL')
      };
    });
  }, [transactions]);

  if (chartData.length < 2) return null;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'var(--bg-surface)',
          padding: '8px 12px',
          border: '1px solid var(--border-light)',
          borderRadius: '8px',
          boxShadow: 'var(--shadow-sm)',
          direction: 'rtl'
        }}>
          <p className="label" style={{ margin: 0, fontWeight: 600, fontSize: '12px' }}>{data.fullDate}</p>
          <p className="price" style={{ margin: '4px 0 0 0', color: 'var(--text-primary)', fontWeight: 700 }}>
            ₪{data.price.toLocaleString()} למ"ר
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-container">
      <h4 className="section-subtitle">מגמת מחירים (₪/מ"ר)</h4>
      <div className="chart-wrapper" style={{ height: 300, paddingRight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-data-emerald)" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="var(--color-data-emerald)" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-light)" />
            <XAxis 
              dataKey="displayDate" 
              tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
              tickMargin={10}
              axisLine={false}
              tickLine={false}
              minTickGap={30}
            />
            <YAxis 
              tickFormatter={(value) => `₪${(value/1000).toFixed(0)}k`}
              tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
              axisLine={false}
              tickLine={false}
              domain={['auto', 'auto']}
              orientation="left" // Force Y axis to left side (LTR standard for numbers even in RTL context usually, or better for layout)
              // In RTL layout, YAxis on right might be preferred, but Recharts can be tricky. 
              // Let's try putting it on the right by mirroring? No, let's stick to left or right prop.
              // orientation="right" matches the previous SVG layout?
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="price"
              stroke="var(--color-data-emerald)"
              fillOpacity={1}
              fill="url(#colorPrice)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// RoomSegmentation now receives the SAME filtered transactions as the table
// This ensures consistency between the room cards and the transactions table
const RoomSegmentation = ({ transactions }) => {
  const roomStats = useMemo(() => {
    if (!transactions || transactions.length === 0) return null;

    const buckets = {};

    // Group by room count - transactions are already filtered for outliers and date
    transactions.forEach(tx => {
      const rooms = tx.beds || tx.rooms; // handle varying property names
      if (!rooms || rooms === 0) return;

      // Calculate amount if missing but pricePerMeter and size exist
      let amount = tx.amount || tx.price;
      if (!amount && tx.pricePerMeter && tx.size) {
        amount = tx.pricePerMeter * tx.size;
      }

      // Basic sanity check: > 100,000 NIS
      if (!amount || amount < 100000) return;

      if (!buckets[rooms]) {
        buckets[rooms] = [];
      }
      buckets[rooms].push(amount);
    });

    // Process each room group
    return Object.entries(buckets)
      .map(([rooms, amounts]) => {
        const sum = amounts.reduce((a, b) => a + b, 0);
        const avg = sum / amounts.length;
        
        return {
          rooms: parseFloat(rooms),
          avgPrice: Math.round(avg),
          count: amounts.length,
          min: Math.min(...amounts),
          max: Math.max(...amounts)
        };
      })
      .sort((a, b) => a.rooms - b.rooms);
  }, [transactions]);

  if (!roomStats || roomStats.length === 0) return null;

  return (
    <div className="room-stats-section">
      <h4 className="section-subtitle">מחירי דירות לפי חדרים (שנתיים אחרונות)</h4>
      <div className="room-stats-grid">
        {roomStats.map(stat => (
          <div key={stat.rooms} className="room-stat-card">
            <div className="room-count">
              <i className="fi fi-rr-bed-alt"></i>
              <span>{stat.rooms} חד'</span>
            </div>
            <div className="room-avg-price">
              ₪{(stat.avgPrice / 1000000).toFixed(2)}M
            </div>
            <div className="room-price-range">
              טווח: {(stat.min / 1000000).toFixed(2)}M - {(stat.max / 1000000).toFixed(2)}M
            </div>
            <div className="room-sample-size">
              {stat.count} עסקאות
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketData = ({ marketData, pricePerSqm }) => {
  const [filterRooms, setFilterRooms] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'score', direction: 'asc' });

  const transactions = marketData?.transactions;

  // 1. First Pass: Filter Outliers & Calculate Score (Memoized)
  const baseTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    
    // Calculate price per meter stats for outlier detection
    const prices = transactions.map(t => t.pricePerMeter).filter(p => p > 0);
    const avgPrice = prices.reduce((sum, p) => sum + p, 0) / prices.length;
    
    const variance = prices.reduce((sum, p) => sum + Math.pow(p - avgPrice, 2), 0) / prices.length;
    const stdDev = Math.sqrt(variance);
    
    const upperLimit = avgPrice + (2 * stdDev);
    const lowerLimit = Math.max(0, avgPrice - (2 * stdDev));

    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    
    return transactions
      .filter(t => {
        if (!t.date) return false;
        // Filter out price outliers
        if (t.pricePerMeter > upperLimit || t.pricePerMeter < lowerLimit) return false;
        const date = new Date(t.date);
        return date >= twoYearsAgo && !isNaN(date.getTime());
      })
      .map(t => {
        // Calculate Score
        const date = new Date(t.date);
        const daysOld = (new Date() - date) / (1000 * 60 * 60 * 24);
        const distance = t.distance !== undefined ? t.distance : 1000;
        // Lower score is better
        const score = (distance * 1.5) + (daysOld * 1.0);
        return { ...t, score };
      });
  }, [transactions]);

  // 2. Second Pass: Filter by Rooms & Select Top 100 "Smart Matches"
  const topTransactions = useMemo(() => {
    let result = [...baseTransactions];

    // A. Filter by Rooms first (so we get the best 100 of the selected type)
    if (filterRooms !== 'all') {
      const targetRooms = parseFloat(filterRooms);
      result = result.filter(t => {
        const rooms = t.beds || t.rooms;
        return rooms === targetRooms; 
      });
    }

    // B. Always sort by "Smart Score" first to define the "Best 100"
    // This ensures the list content remains stable regardless of UI sorting
    result.sort((a, b) => (a.score || Infinity) - (b.score || Infinity));

    // C. Take the top 100
    return result.slice(0, 100);
  }, [baseTransactions, filterRooms]);

  // 3. Third Pass: Apply User UI Sorting to the Top 100
  const processedTransactions = useMemo(() => {
    let result = [...topTransactions];

    // Sort the existing 100 items by the user's chosen column
    if (sortConfig.key !== 'score') {
      result.sort((a, b) => {
        let valA, valB;

        switch (sortConfig.key) {
          case 'address':
            valA = a.address || '';
            valB = b.address || '';
            return sortConfig.direction === 'asc' 
              ? valA.localeCompare(valB) 
              : valB.localeCompare(valA);
          case 'distance':
            valA = (a.distance !== undefined && a.distance !== null) ? a.distance : Infinity;
            valB = (b.distance !== undefined && b.distance !== null) ? b.distance : Infinity;
            break;
          case 'date':
            valA = new Date(a.date).getTime();
            valB = new Date(b.date).getTime();
            break;
          case 'amount':
            valA = a.amount || 0;
            valB = b.amount || 0;
            break;
          case 'size':
            valA = a.size || 0;
            valB = b.size || 0;
            break;
          case 'beds':
            valA = a.beds || 0;
            valB = b.beds || 0;
            break;
          case 'pricePerMeter':
            valA = a.pricePerMeter || 0;
            valB = b.pricePerMeter || 0;
            break;
          default:
            return 0;
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [topTransactions, sortConfig]);

  // Calculate derived stats from visible filtered transactions
  const derivedStats = useMemo(() => {
    if (processedTransactions.length === 0) return null;
    
    const prices = processedTransactions.map(t => t.pricePerMeter);
    const minPrice = Math.round(Math.min(...prices));
    const maxPrice = Math.round(Math.max(...prices));
    const avgPrice = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
    
    return {
      priceRange: { min: minPrice, max: maxPrice },
      averagePrice: avgPrice
    };
  }, [processedTransactions]);

  // Helper for sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (name) => {
    if (sortConfig.key !== name) return <i className="fi fi-rr-sort-alt opacity-30"></i>;
    return sortConfig.direction === 'asc' 
      ? <i className="fi fi-rr-sort-amount-down-alt"></i> 
      : <i className="fi fi-rr-sort-amount-up-alt"></i>;
  };

  if (!marketData && !pricePerSqm) return null;

  // Check if we have real market data (transactions, pricePerMeter from API, or insights)
  const hasRealTransactions = transactions && transactions.length > 0;
  const hasRealPriceData = marketData?.pricePerMeter && marketData.pricePerMeter > 0 && marketData?.dataSource !== 'fallback_estimate';
  const hasMarketInsights = marketData?.marketInsights && marketData.marketInsights.length > 0;
  const hasRealData = hasRealTransactions || hasRealPriceData || hasMarketInsights;
  
  // If we have no real data at all - show graceful "no data" message
  if (!hasRealData) {
    return (
      <div className="market-data-card no-data-state">
        <div className="market-data-header">
          <h3 className="market-data-title"><i className="fi fi-rr-chart-histogram"></i> נתוני שוק ומחירים</h3>
        </div>
        <div className="market-data-content">
          <div className="no-market-data-container">
            <div className="no-data-icon">
              <i className="fi fi-rr-chart-line-up"></i>
            </div>
            <h4 className="no-data-title">לא נמצאו נתוני שוק עבור כתובת זו</h4>
            <p className="no-data-description">
              לא הצלחנו לאתר עסקאות נדל"ן או נתוני מחירים עדכניים עבור הנכס הזה.
              <br />
              ייתכן שהכתובת חדשה או שאין מספיק עסקאות מתועדות באזור.
            </p>
            <div className="no-data-suggestions">
              <span className="suggestion-label">מה ניתן לעשות:</span>
              <ul>
                <li>לבדוק נכסים סמוכים באותו הרחוב</li>
                <li>לפנות לשמאי מקרקעין לקבלת הערכה מקצועית</li>
                <li>לבדוק באתרי נדל"ן נוספים (מדלן, יד2)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Destructure data for main view
  const { pricePerMeter, priceRange, dataSource } = marketData || {};
  const isRealData = dataSource === 'market_source' || dataSource === 'market_data';
  
  const displayPriceRange = derivedStats?.priceRange || priceRange;
  const displayAvgPrice = derivedStats?.averagePrice || pricePerMeter || pricePerSqm;
  const hasDisplayAvgPrice = Number.isFinite(displayAvgPrice) && displayAvgPrice > 0 && dataSource !== 'fallback_estimate';
  const hasDisplayRange = displayPriceRange 
    && Number.isFinite(displayPriceRange.min) 
    && Number.isFinite(displayPriceRange.max) 
    && displayPriceRange.min > 0 
    && displayPriceRange.max > 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  // Extract unique room counts for filter
  const availableRoomCounts = [...new Set(baseTransactions.map(t => t.beds || t.rooms))].sort((a,b) => a-b);

  return (
    <div className={`market-data-card ${isRealData ? 'real-data' : 'fallback-data'}`}>
      <div className="market-data-header">
        <h3 className="market-data-title"><i className="fi fi-rr-chart-histogram"></i> נתוני שוק ומחירים</h3>
        {isRealData && (
          <span className="data-badge real"><i className="fi fi-rr-check"></i> נתוני אמת</span>
        )}
      </div>

      <div className="market-data-content">
        {/* Pricing Highlights - only if we have a real price */}
        {hasDisplayAvgPrice && (
          <div className="pricing-grid">
            <div className="price-box main-price">
              <span className="price-label">מחיר ממוצע למ"ר</span>
              <span className="price-value">₪{displayAvgPrice.toLocaleString()}</span>
            </div>
            
            {hasDisplayRange && (
              <div className="price-box range-price">
                <span className="price-label">טווח מחירים למ"ר באזור</span>
                <span className="price-value-small">
                  ₪{displayPriceRange.min.toLocaleString()} - ₪{displayPriceRange.max.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Split View Container */}
        <div className="market-data-split-container">
          
          {/* Right Column: Charts & Room Stats */}
          <div className="market-data-column right-column">
            {/* Price Trend Chart - uses ALL historical data for better trend visibility */}
            {transactions && transactions.length > 1 ? (
              <PriceTrendChart transactions={transactions} />
            ) : (
              <div className="no-data-message">
                <i className="fi fi-rr-chart-histogram"></i>
                <span>לא נמצאו מספיק נתונים היסטוריים להצגת גרף מגמה עבור נכס זה</span>
              </div>
            )}

            {/* Room Segmentation - uses same filtered data as the table for consistency */}
            {baseTransactions && baseTransactions.length > 0 && (
              <RoomSegmentation transactions={baseTransactions} />
            )}
          </div>

          {/* Left Column: Recent Transactions Table */}
          <div className="market-data-column left-column">
            {/* Recent Transactions */}
            <div className="transactions-section">
              <div className="section-header-row">
                <h4 className="section-subtitle">עסקאות אחרונות (שנתיים אחרונות)</h4>
                
                {/* Room Filter */}
                <div className="room-filter">
                  <span className="filter-label">חדרים:</span>
                  <select 
                    id="market-rooms-filter"
                    name="rooms_filter"
                    autoComplete="off"
                    value={filterRooms} 
                    onChange={(e) => setFilterRooms(e.target.value)}
                    className="room-filter-select"
                  >
                    <option value="all">הכל</option>
                    {availableRoomCounts.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>

              {processedTransactions.length > 0 ? (
                <div className="transactions-table-wrapper">
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th onClick={() => requestSort('address')} className="sortable">
                          כתובת {getSortIcon('address')}
                        </th>
                        <th onClick={() => requestSort('distance')} className="sortable">
                          מרחק {getSortIcon('distance')}
                        </th>
                        <th onClick={() => requestSort('date')} className="sortable">
                          תאריך {getSortIcon('date')}
                        </th>
                        <th onClick={() => requestSort('amount')} className="sortable">
                          מחיר {getSortIcon('amount')}
                        </th>
                        <th onClick={() => requestSort('size')} className="sortable">
                          שטח {getSortIcon('size')}
                        </th>
                        <th onClick={() => requestSort('beds')} className="sortable">
                          חדרים {getSortIcon('beds')}
                        </th>
                        <th onClick={() => requestSort('pricePerMeter')} className="sortable">
                          מחיר למ"ר {getSortIcon('pricePerMeter')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedTransactions.map((tx, index) => (
                        <tr key={index}>
                          <td className="address-cell">{tx.address || '-'}</td>
                          <td>{tx.distance !== undefined ? `${Math.round(tx.distance)} מ'` : '-'}</td>
                          <td>{formatDate(tx.date)}</td>
                          <td>₪{Math.round(tx.amount).toLocaleString()}</td>
                          <td>{tx.size} מ"ר</td>
                          <td>{tx.beds}</td>
                          <td>₪{Math.round(tx.pricePerMeter).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                 <div className="no-data-message">
                    <i className="fi fi-rr-time-past"></i>
                    <span>לא נמצאו עסקאות תואמות לסינון זה</span>
                 </div>
              )}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default MarketData;
